import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  IconButton,
} from '@mui/material';
import { X as CloseIcon, Check as CheckIcon, Clock as ClockIcon } from 'lucide-react';
import { formatDistance } from 'date-fns';
import { es } from 'date-fns/locale';
import { useRoutineStore } from '../store/routineStore';
import { useWorkoutStore } from '../store/workoutStore';
import { useExerciseStore } from '../store/exerciseStore';
import { useProgressStore } from '../store/progressStore';
import { WorkoutSetInput } from '../components/WorkoutInputs';
import { useLoadPlanStore } from '../store/loadPlanStore';
import { targetAt, todayKey } from '../utils/loadProgression';

const Workout = ({ routineId, dayId, onClose }) => {
  const routine = useRoutineStore((state) => state.getRoutineById(routineId));
  const activeWorkout = useWorkoutStore((state) => state.activeWorkout);
  const addExerciseToWorkout = useWorkoutStore((state) => state.addExerciseToWorkout);
  const updateSetCompletion = useWorkoutStore((state) => state.updateSetCompletion);
  const finishWorkout = useWorkoutStore((state) => state.finishWorkout);
  const cancelWorkout = useWorkoutStore((state) => state.cancelWorkout);
  const exercises = useExerciseStore((state) => state.exercises);
  const updateStreak = useProgressStore((state) => state.updateStreak);

  const loadPlans = useLoadPlanStore((state) => state.plans);

  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [finishDialogOpen, setFinishDialogOpen] = useState(false);
  const [durationSeconds, setDurationSeconds] = useState(0);
  const exercisesInitialized = React.useRef(false);

  // Timer useEffect
  useEffect(() => {
    const timer = setInterval(() => {
      setDurationSeconds((s) => s + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Initialize exercises useEffect
  useEffect(() => {
    if (!activeWorkout) return;
    // Si el workout activo corresponde a otra rutina/día, no cargar sus ejercicios
    if (activeWorkout.routineId !== routineId || activeWorkout.dayId !== dayId) return;
    if (activeWorkout.exercises.length > 0) return;
    // Guardia contra doble ejecución (React StrictMode)
    if (exercisesInitialized.current) return;
    exercisesInitialized.current = true;

    const day = routine?.days.find((d) => d.id === dayId);
    if (!day?.exercises) return;

    day.exercises.forEach((ex) => {
      const exercise = exercises.find((e) => e.id === ex.exerciseId);
      if (exercise) {
        addExerciseToWorkout({
          exerciseId: exercise.id,
          exerciseName: exercise.name,
          sets: Array.from({ length: ex.sets }).map(() => ({
            reps: ex.reps,
            weight: exercise.defaultWeight || 0,
            completed: false,
          })),
        });
      }
    });
  }, [activeWorkout, routine, dayId, exercises, addExerciseToWorkout]);

  if (!activeWorkout) {
    return null;
  }

  // Si el workout activo es de otra rutina/día, no mostrar nada
  if (activeWorkout.routineId !== routineId || activeWorkout.dayId !== dayId) {
    return null;
  }

  const day = routine?.days.find((d) => d.id === dayId);

  const currentExercise = activeWorkout.exercises[currentExerciseIndex];
  const loadTarget = targetAt(loadPlans.find((plan) => plan.exerciseId === currentExercise?.exerciseId), todayKey());
  const exerciseProgress = Math.round(
    (currentExerciseIndex / (activeWorkout.exercises.length || 1)) * 100
  );

  const handleSetComplete = (reps, weight) => {
    updateSetCompletion(currentExerciseIndex, reps, weight, weight);
    if (currentExerciseIndex < activeWorkout.exercises.length - 1) {
      setCurrentExerciseIndex(currentExerciseIndex + 1);
    }
  };

  const handleFinish = () => {
    finishWorkout();
    updateStreak(new Date().toISOString());
    setFinishDialogOpen(false);
    onClose();
  };

  const minutes = Math.floor(durationSeconds / 60);
  const seconds = durationSeconds % 60;
  const formattedTime = `${minutes}:${seconds.toString().padStart(2, '0')}`;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: 'background.default',
        zIndex: 1300,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {day?.name}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            {routine?.name}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ textAlign: 'right' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ClockIcon size={18} />
              <Typography variant="h6" sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>
                {formattedTime}
              </Typography>
            </Box>
            <Typography variant="caption">
              {currentExerciseIndex + 1} / {activeWorkout.exercises.length}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setFinishDialogOpen(true)}
            sx={{ color: 'white' }}
          >
            <CloseIcon size={24} />
          </IconButton>
        </Box>
      </Box>

      {/* Progress Bar */}
      <LinearProgress
        variant="determinate"
        value={exerciseProgress}
        sx={{ height: 4, bgcolor: 'rgba(255,255,255,0.2)' }}
      />

      {/* Main Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {currentExercise && loadTarget != null && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="subtitle2">Objetivo de carga hoy: {loadTarget.toLocaleString('es')} kg</Typography>
            <Typography variant="caption" color="text.secondary">Máximo previsto en tu plan. Registra en cada serie el peso que realmente ejecutes.</Typography>
          </Box>
        )}
        {currentExercise ? (
          <WorkoutSetInput
            key={currentExerciseIndex}
            exerciseName={currentExercise.exerciseName}
            sets={currentExercise.sets}
            onSetToggle={(sets) => updateSetCompletion(currentExerciseIndex, sets)}
            onComplete={(sets) => {
              updateSetCompletion(currentExerciseIndex, sets);
              if (currentExerciseIndex < activeWorkout.exercises.length - 1) {
                setCurrentExerciseIndex(currentExerciseIndex + 1);
              }
            }}
          />
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6">¡Excelente trabajo!</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
              Todos los ejercicios completados
            </Typography>
          </Box>
        )}
      </Box>

      {/* Action Buttons */}
      <Box sx={{ p: 2, display: 'flex', gap: 1, bgcolor: 'background.paper' }}>
        {currentExerciseIndex < activeWorkout.exercises.length - 1 && (
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setCurrentExerciseIndex(currentExerciseIndex + 1)}
          >
            Siguiente
          </Button>
        )}
        {currentExerciseIndex === activeWorkout.exercises.length - 1 && (
          <Button
            fullWidth
            variant="contained"
            color="success"
            onClick={() => setFinishDialogOpen(true)}
            startIcon={<CheckIcon size={18} />}
          >
            Terminar
          </Button>
        )}
      </Box>

      {/* Finish Dialog */}
      <Dialog
        open={finishDialogOpen}
        onClose={() => setFinishDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Terminar Entrenamiento</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              Resumen:
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              ⏱️ Duración: {minutes}min {seconds}s
            </Typography>
            <Typography variant="body2">
              🏋️ Ejercicios: {activeWorkout.exercises.length}
            </Typography>
          </Box>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notas del entrenamiento"
            placeholder="¿Cómo te sentiste? ¿Qué cambiarías?"
            defaultValue={activeWorkout.notes}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFinishDialogOpen(false)}>Seguir</Button>
          <Button
            onClick={handleFinish}
            variant="contained"
            color="success"
          >
            Guardar y Terminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Workout;
